/**
 * 多物件並行管理視圖 | Multi-Object Parallel Management View
* 以物件為單位的流程追蹤與監控
* 注重實時狀態、視覺化追蹤、群組化處理
 */

import { useState, useMemo } from 'react';
import { useOrderStore, useResourceStore } from '@/store';

type ViewMode = 'cards' | 'timeline' | 'summary';
type GroupBy = 'status' | 'product' | 'process' | 'none';

export function MultiObjectTracking() {
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [groupBy, setGroupBy] = useState<GroupBy>('status');

  const orders = useOrderStore((state) => state.orders);
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const products = useResourceStore((state) => state.products);

  // 建立物件追蹤數據（每個物件 = 一個訂單的生產生命週期）
  const objectTracking = useMemo(() => {
    return orders.map((order) => {
      const relatedMainOrders = mainWorkOrders.filter((mwo) => mwo.orderId === order.id);
      const relatedSubOrders = relatedMainOrders.flatMap((mwo) =>
        subWorkOrders.filter((swo) => swo.mainOrderId === mwo.id)
      );

      // 計算整體進度
      const totalPlanned = relatedMainOrders.reduce((sum, mwo) => sum + mwo.totalQuantity, 0);
      const totalActual = relatedMainOrders.reduce((sum, mwo) => sum + mwo.completedQuantity, 0);
      const overallProgress = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 100) : 0;

      // 獲取當前所在工序
      const currentProcess = relatedSubOrders.find((swo) => swo.status === '進行中');
      const currentStage = currentProcess ? currentProcess.processName : '未開始';

      // 計算已完成工序數
      const completedStages = relatedSubOrders.filter((swo) => swo.status === '已完成').length;
      const totalStages = relatedSubOrders.length;

      return {
        order,
        mainOrders: relatedMainOrders,
        subOrders: relatedSubOrders,
        progress: overallProgress,
        currentStage,
        completedStages,
        totalStages,
        status: order.status,
        productId: order.productId,
      };
    });
  }, [orders, mainWorkOrders, subWorkOrders]);

  // 群組化邏輯
  const groupedData = useMemo(() => {
    if (groupBy === 'none') {
      return { '全部': objectTracking };
    }

    if (groupBy === 'status') {
      const groups = new Map<string, typeof objectTracking>();
      objectTracking.forEach((obj) => {
        const key = obj.status;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(obj);
      });
      return Object.fromEntries(groups);
    }

    if (groupBy === 'product') {
      const groups = new Map<string, typeof objectTracking>();
      objectTracking.forEach((obj) => {
        const key = obj.productId;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(obj);
      });
      return Object.fromEntries(groups);
    }

    if (groupBy === 'process') {
      const groups = new Map<string, typeof objectTracking>();
      objectTracking.forEach((obj) => {
        const key = obj.currentStage;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(obj);
      });
      return Object.fromEntries(groups);
    }

    return { '全部': objectTracking };
  }, [objectTracking, groupBy]);

  // 獲取狀態配置
  const getStatusConfig = (status: string) => {
    switch (status) {
      case '已完成':
        return {
          bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
          border: 'border-green-300',
          text: 'text-green-700',
          icon: '✓',
          progressColor: 'from-green-500 to-emerald-400',
        };
      case '進行中':
        return {
          bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
          border: 'border-amber-300',
          text: 'text-amber-700',
          icon: '⚡',
          progressColor: 'from-amber-500 to-orange-400',
        };
      case '已延遲':
        return {
          bg: 'bg-gradient-to-br from-red-50 to-rose-50',
          border: 'border-red-300',
          text: 'text-red-700',
          icon: '⚠️',
          progressColor: 'from-red-500 to-rose-400',
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-slate-50 to-zinc-50',
          border: 'border-slate-300',
          text: 'text-slate-700',
          icon: '⏳',
          progressColor: 'from-slate-400 to-zinc-400',
        };
    }
  };

  // 統計數據
  const statistics = useMemo(() => {
    return {
      total: objectTracking.length,
      completed: objectTracking.filter((o) => o.status === '已完成').length,
      inProgress: objectTracking.filter((o) => o.status === '進行中').length,
      delayed: objectTracking.filter((o) => o.status === '已延遲').length,
      avgProgress: Math.round(
        objectTracking.reduce((sum, o) => sum + o.progress, 0) / objectTracking.length
      ),
      byProduct: new Map(
        Object.entries(
          objectTracking.reduce((acc, o) => {
            acc[o.productId] = (acc[o.productId] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        )
      ),
    };
  }, [objectTracking]);

  return (
    <div className="space-y-4">
      {/* 標題與控制列 */}
      <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 左側：標題 */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
              <span className="text-white text-2xl">🔄</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">多物件並行管理</h2>
              <p className="text-sm text-slate-600">以物件為單位的流程追蹤與監控</p>
            </div>
          </div>

          {/* 右側：統計 */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="px-4 py-2 bg-white/70 border-2 border-violet-300 rounded-lg">
              <span className="text-xs font-bold text-violet-700">總物件數</span>
              <div className="text-2xl font-black text-violet-600">{statistics.total}</div>
            </div>
            <div className="px-4 py-2 bg-white/70 border-2 border-green-300 rounded-lg">
              <span className="text-xs font-bold text-green-700">已完成</span>
              <div className="text-2xl font-black text-green-600">{statistics.completed}</div>
            </div>
            <div className="px-4 py-2 bg-white/70 border-2 border-amber-300 rounded-lg">
              <span className="text-xs font-bold text-amber-700">進行中</span>
              <div className="text-2xl font-black text-amber-600">{statistics.inProgress}</div>
            </div>
            <div className="px-4 py-2 bg-white/70 border-2 border-violet-300 rounded-lg">
              <span className="text-xs font-bold text-violet-700">平均進度</span>
              <div className="text-2xl font-black text-violet-600">{statistics.avgProgress}%</div>
            </div>
          </div>
        </div>

        {/* 控制按鈕 */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {/* 視圖模式切換 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">視圖：</label>
            <div className="flex bg-white border-2 border-violet-200 rounded-lg shadow-sm overflow-hidden">
              {[
                { value: 'cards', label: '卡片', icon: '📇' },
                { value: 'timeline', label: '時間軸', icon: '📊' },
                { value: 'summary', label: '摘要', icon: '📋' },
              ].map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setViewMode(mode.value as ViewMode)}
                  className={`px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 ${
                    viewMode === mode.value
                      ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white'
                      : 'text-violet-700 hover:bg-violet-50'
                  }`}
                >
                  <span>{mode.icon}</span>
                  <span>{mode.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 群組切換 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">群組：</label>
            <div className="flex bg-white border-2 border-violet-200 rounded-lg shadow-sm overflow-hidden">
              {[
                { value: 'status', label: '狀態' },
                { value: 'product', label: '產品' },
                { value: 'process', label: '工序' },
                { value: 'none', label: '無' },
              ].map((group) => (
                <button
                  key={group.value}
                  onClick={() => setGroupBy(group.value as GroupBy)}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${
                    groupBy === group.value
                      ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white'
                      : 'text-violet-700 hover:bg-violet-50'
                  }`}
                >
                  {group.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 內容區域 */}
      {viewMode === 'cards' && (
        <>
          {objectTracking.length === 0 ? (
            <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-12 text-center shadow-sm">
              <div className="text-6xl mb-4">🔄</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">暫無物件資料</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                目前沒有生產中的物件。建立訂單後，系統會自動開始追蹤其生產生命週期
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => window.location.href = '#orders'}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  前往訂單管理
                </button>
                <button
                  onClick={() => window.location.href = '#overview'}
                  className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
                >
                  返回概覽
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedData).map(([groupName, items]) => (
            <div key={groupName}>
              {/* 群組標題 */}
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-xl font-bold text-slate-800">{groupName}</h3>
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold border border-violet-300">
                  {items.length} 個物件
                </span>
              </div>

              {/* 卡片網格 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((item) => {
                  const statusConfig = getStatusConfig(item.status);
                  const product = products.find((p) => p.id === item.productId);

                  return (
                    <div
                      key={item.order.id}
                      className={`${statusConfig.bg} border-2 ${statusConfig.border} rounded-xl p-5 shadow-lg hover:shadow-xl transition-all`}
                    >
                      {/* 標題 */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-2xl`}>{statusConfig.icon}</span>
                            <span className="text-lg font-bold text-slate-800">{item.order.id}</span>
                          </div>
                          <p className="text-sm text-slate-600">{product?.name || '未知產品'}</p>
                          <p className="text-sm text-slate-600">客戶：{item.order.customer}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                          {item.status}
                        </span>
                      </div>

                      {/* 進度條 */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-700">整體進度</span>
                          <span className={`text-lg font-bold ${statusConfig.text}`}>{item.progress}%</span>
                        </div>
                        <div className="w-full h-3 bg-white/70 rounded-full overflow-hidden shadow-inner">
                          <div
                            className={`h-full bg-gradient-to-r ${statusConfig.progressColor} transition-all duration-500`}
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      </div>

                      {/* 工序進度 */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-700">工序進度</span>
                          <span className="text-sm text-slate-600">
                            {item.completedStages} / {item.totalStages}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {Array.from({ length: item.totalStages }).map((_, index) => (
                            <div
                              key={index}
                              className={`flex-1 h-2 rounded-full transition-all ${
                                index < item.completedStages
                                  ? 'bg-gradient-to-r from-violet-500 to-purple-500'
                                  : index === item.completedStages && item.status === '進行中'
                                  ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                                  : 'bg-slate-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* 當前階段 */}
                      <div className="bg-white/70 border border-slate-300 rounded-lg p-3">
                        <div className="text-xs text-slate-500 mb-1">當前階段</div>
                        <div className="text-sm font-bold text-slate-800">{item.currentStage}</div>
                      </div>

                      {/* 快速操作 */}
                      <div className="mt-4 flex gap-2">
                        <button className="flex-1 px-3 py-2 bg-white/70 border-2 border-violet-300 rounded-lg text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-all">
                          查看詳情
                        </button>
                        <button className="px-3 py-2 bg-white/70 border-2 border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all">
                          追蹤
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}

      {viewMode === 'timeline' && (
        <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-violet-50 to-white border-b-2 border-violet-200 px-6 py-4">
            <h3 className="text-lg font-bold text-slate-800">時間軸視圖</h3>
          </div>
          <div className="p-6">
            {objectTracking.length === 0 ? (
              <div className="bg-gradient-to-br from-violet-50 to-white border-2 border-violet-200 rounded-xl p-12 text-center shadow-sm">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">暫無物件資料</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">建立訂單後，時間軸將顯示每個物件的生產進度</p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => window.location.href = '#orders'}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
                  >
                    前往訂單管理
                  </button>
                </div>
              </div>
            ) : (
              objectTracking.map((item) => {
              const statusConfig = getStatusConfig(item.status);
              return (
                <div key={item.order.id} className="mb-6 last:mb-0">
                  <div className="flex items-center gap-4 mb-3">
                    <div className={`w-3 h-3 rounded-full ${statusConfig.icon === '✓' ? 'bg-green-500' : statusConfig.icon === '⚡' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800">{item.order.id}</span>
                        <span className={`text-sm font-bold ${statusConfig.text}`}>{item.progress}%</span>
                      </div>
                      <div className="text-sm text-slate-600">{item.order.productName}</div>
                    </div>
                  </div>

                  {/* 工序時間軸 */}
                  <div className="ml-6 space-y-2">
                    {item.subOrders.map((subOrder, index) => (
                      <div
                        key={subOrder.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          subOrder.status === '已完成'
                            ? 'bg-green-50 border-green-300'
                            : subOrder.status === '進行中'
                            ? 'bg-amber-50 border-amber-300'
                            : 'bg-slate-50 border-slate-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                          subOrder.status === '已完成'
                            ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                            : subOrder.status === '進行中'
                            ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                            : 'bg-slate-400'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-800">{subOrder.processName}</div>
                          <div className="text-sm text-slate-600">
                            {subOrder.actualQuantity} / {subOrder.plannedQuantity}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          subOrder.status === '已完成'
                            ? 'bg-green-100 text-green-700'
                            : subOrder.status === '進行中'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {subOrder.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
          </div>
        </div>
      )}

      {viewMode === 'summary' && (
        <>
          {objectTracking.length === 0 ? (
            <div className="bg-gradient-to-br from-violet-50 to-white border-2 border-violet-200 rounded-xl p-12 text-center shadow-sm">
              <div className="text-6xl mb-4">📈</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">暫無統計資料</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">建立訂單後，摘要視圖將顯示生產統計與分析</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => window.location.href = '#orders'}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
                >
                  前往訂單管理
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* 總物件數 */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-violet-700 mb-2">總物件數</div>
                <div className="text-3xl font-black text-violet-600">{statistics.total}</div>
              </div>

              {/* 已完成 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-green-700 mb-2">已完成</div>
                <div className="text-3xl font-black text-green-600">{statistics.completed}</div>
                <div className="text-xs text-green-600 mt-1">
                  {Math.round((statistics.completed / statistics.total) * 100)}% 完成率
                </div>
              </div>

              {/* 進行中 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-amber-700 mb-2">進行中</div>
                <div className="text-3xl font-black text-amber-600">{statistics.inProgress}</div>
                <div className="text-xs text-amber-600 mt-1">
                  活躍生產中
                </div>
              </div>

              {/* 平均進度 */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-blue-700 mb-2">平均進度</div>
                <div className="text-3xl font-black text-blue-600">{statistics.avgProgress}%</div>
                <div className="text-xs text-blue-600 mt-1">
                  整體完成度
                </div>
              </div>

              {/* 按產品分組統計 */}
              {Array.from(statistics.byProduct.entries()).map(([productId, count]) => {
                const product = products.find((p) => p.id === productId);
                return (
                  <div
                    key={productId}
                    className="bg-gradient-to-br from-slate-50 to-zinc-50 border-2 border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="text-sm font-medium text-slate-700 mb-2">{product?.name || productId}</div>
                    <div className="text-3xl font-black text-slate-600">{count}</div>
                    <div className="text-xs text-slate-500 mt-1">生產中物件數</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
